/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import descriptors from '../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  IRequestContext
} from '../DashboardKPIWidgets/IEnergyManagementProps';
import useMounted from '../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { Card } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import withLoadingStatus from '../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../shared/StatusWrappers/ErrorStatus';
import EnergyOptimizationSettingsComponentContent from './EnergyOptimizationSettingsComponentContent';

import './EnergyOptimizationSettingsCommon.scss';

interface EnergyOptimizationSettingContentProps {
  data: boolean;
  bearerToken?: string;
  requestContext: IRequestContext;
}

const EnergyOptimizationSettingContent = (
  props: EnergyOptimizationSettingContentProps
) => {
  const { data, requestContext, bearerToken } = props;

  return (
    <EnergyOptimizationSettingsComponentContent
      hasEOEnabled={data}
      requestContext={requestContext}
      bearerToken={bearerToken}
    />
  );
};

const EnergyOptimizationSettingsComponentWithLoader = withLoadingStatus(
  withErrorStatus(EnergyOptimizationSettingContent)
);

const EnergyOptimizationSettingsComponent = (props: IEnergyManagementProps) => {
  const { bearerToken, requestContext } = props;

  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  const connectorConfig = {
    name: 'get-energy-optimization-status'
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [requestContext]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const { all, loading, error, status } = dataConnector.data;

  const energyOptimizationSetting = (all as unknown) as boolean;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState(false);

  useEffect(() => {
    setIsLoading(loading);
    if ((error || status) && !loading) {
      if (
        energyOptimizationSetting &&
        typeof energyOptimizationSetting === 'boolean'
      ) {
        setData(energyOptimizationSetting);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [energyOptimizationSetting, loading, status]);

  return (
    <div className="emaas-energy-optimization-settings">
      <div className="energy-optimization-setting-section">
        <div>
          <div className="energy-optimization-setting-header">
            <div className="energy-optimization-setting-title">
              {t(d.ENERGYOPTIMIZATIONEO)}
            </div>
          </div>
          <div className="energy-optimization-setting-content">
            <span>{t(d.EOSETTINGSUBTITLE)}</span>
          </div>
        </div>
        <Card.Content className="energy-optimization-setting-content-section">
          <EnergyOptimizationSettingsComponentWithLoader
            loading={isLoading}
            loadingText={t(d.LOADING)}
            data={data}
            onRefresh={onRefresh}
            title={t(d.CONNECTIONERRORTITLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            statusCode={statusCode}
            error={errorInfo}
            noDataMessage={t(d.EOSETTINGNODATAAVAILABLE)}
            requestContext={requestContext}
            bearerToken={props.bearerToken}
          />
        </Card.Content>
      </div>
    </div>
  );
};
export default EnergyOptimizationSettingsComponent;
